/**
 * AriaCreateWizard — Wizard 4 step per creare un'istanza Aria.
 * Step 1: Sito (URL/nome/privacy email)
 * Step 2: Verticale (categoria business)
 * Step 3: Brand (colori + posizione widget)
 * Step 4: Persona (nome Aria + tono)
 * Step 5 (review): Conferma e crea
 */
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowLeft, ArrowRight, Check, Sparkles, Globe, Tag, Palette, User,
    Loader, Utensils, ShoppingBag, Scissors, Dumbbell, Briefcase, Printer,
    Coffee, GraduationCap, Wrench, MoreHorizontal,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const VERTICALS = [
    { key: "ristorante", label: "Ristorante / Pizzeria", icon: Utensils, persona: "Aria del Ristorante", color: "#FF6B3D" },
    { key: "ecommerce", label: "E-commerce / Negozio", icon: ShoppingBag, persona: "Aria Shop Assistant", color: "#A855F7" },
    { key: "bellezza", label: "Bellezza / Estetica", icon: Scissors, persona: "Aria Beauty", color: "#FF1A5C" },
    { key: "fitness", label: "Palestra / Fitness", icon: Dumbbell, persona: "Aria Coach", color: "#86EFAC" },
    { key: "studio", label: "Studio professionale", icon: Briefcase, persona: "Aria Studio", color: "#4F8BFF" },
    { key: "tipografia", label: "Tipografia / Stampa", icon: Printer, persona: "Aria Print", color: "#FFD66B" },
    { key: "bar", label: "Bar / Caffetteria", icon: Coffee, persona: "Aria Café", color: "#FFB13D" },
    { key: "formazione", label: "Formazione / Scuola", icon: GraduationCap, persona: "Aria Tutor", color: "#9EC3FF" },
    { key: "servizi", label: "Servizi alla persona", icon: Wrench, persona: "Aria Assistente", color: "#C7A4FF" },
    { key: "altro", label: "Altro", icon: MoreHorizontal, persona: "Aria", color: "#9EC3FF" },
];

const TONES = [
    { key: "professionale", label: "Professionale", desc: "Formale, preciso, affidabile" },
    { key: "amichevole", label: "Amichevole", desc: "Caldo, vicino, come un amico" },
    { key: "diretto", label: "Diretto", desc: "Conciso, no fronzoli" },
    { key: "premium", label: "Premium", desc: "Raffinato, esclusivo" },
];

const POSITIONS = [
    { key: "bottom-right", label: "Basso destra", emoji: "↘" },
    { key: "bottom-left", label: "Basso sinistra", emoji: "↙" },
];

const PRESET_COLORS = ["#A855F7", "#FF1A5C", "#4F8BFF", "#10B981", "#FFD66B", "#FF6B3D", "#86EFAC", "#FFFFFF"];

const STEPS = [
    { key: "site", label: "Sito", icon: Globe },
    { key: "vertical", label: "Verticale", icon: Tag },
    { key: "brand", label: "Brand", icon: Palette },
    { key: "persona", label: "Persona", icon: User },
    { key: "preview", label: "Anteprima", icon: Sparkles },
];

export default function AriaCreateWizard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [form, setForm] = useState({
        site_url: "",
        site_name: "",
        privacy_email: user?.email || "",
        vertical: "",
        primary_color: "#A855F7",
        secondary_color: "#FF1A5C",
        position: "bottom-right",
        persona_name: "",
        tone_of_voice: "amichevole",
        accept_dpa: false,
    });

    const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const selectedVertical = useMemo(() => VERTICALS.find((v) => v.key === form.vertical), [form.vertical]);

    React.useEffect(() => {
        document.title = "Crea la tua Aria — TotalKlick";
        if (!user) navigate("/login?next=/aria/crea");
    }, [user, navigate]);

    // Quando seleziono verticale → auto-suggerisce persona name + primary color se non personalizzati
    React.useEffect(() => {
        if (selectedVertical && !form.persona_name) {
            upd("persona_name", selectedVertical.persona);
        }
        if (selectedVertical) {
            upd("primary_color", selectedVertical.color);
        }
    }, [form.vertical, selectedVertical, form.persona_name]);

    const canProceed = () => {
        if (step === 0) return form.site_url.trim().length > 3 && form.site_name.trim() && form.privacy_email.includes("@");
        if (step === 1) return !!form.vertical;
        if (step === 2) return !!form.primary_color && !!form.position;
        if (step === 3) return form.persona_name.trim() && !!form.tone_of_voice;
        if (step === 4) return true;
        return true;
    };

    const submit = async () => {
        setSubmitting(true); setError("");
        try {
            const payload = {
                site_url: form.site_url.startsWith("http") ? form.site_url : `https://${form.site_url}`,
                site_name: form.site_name,
                privacy_email: form.privacy_email,
                vertical: form.vertical,
                persona_name: form.persona_name,
                accept_dpa: true,
                custom_instructions: { tone_of_voice: form.tone_of_voice },
                experience_config: {
                    brand: {
                        primary_color: form.primary_color,
                        secondary_color: form.secondary_color,
                    },
                    position: form.position,
                },
            };
            const { data } = await api.post("/widget/register", payload);
            localStorage.setItem("aria.active_widget", data.widget_token);
            navigate("/app/aria/installazione");
        } catch (e) { setError(formatApiError(e)); }
        setSubmitting(false);
    };

    return (
        <div className="min-h-screen px-4 py-8" style={{ background: "#05050F", color: "#fff", fontFamily: "Geist, Outfit, sans-serif" }} data-testid="aria-create-wizard">
            <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(circle at 20% 10%, rgba(168,85,247,0.12), transparent 50%), radial-gradient(circle at 80% 80%, rgba(255,26,92,0.08), transparent 50%)" }} />

            <div className="relative max-w-2xl mx-auto">
                {/* Header */}
                <button type="button" onClick={() => navigate("/app")} className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-6" data-testid="back-workspace">
                    <ArrowLeft size={12} /> Workspace
                </button>

                <div className="text-[10.5px] uppercase tracking-[0.3em] text-white/45 mb-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>Step {step + 1} / 5</div>
                <h1 className="text-white mb-1" style={{ fontFamily: "Geist, sans-serif", fontWeight: 200, fontSize: "clamp(2rem, 4vw, 2.8rem)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                    {step === 0 && "Inizia dal tuo sito"}
                    {step === 1 && "Cosa fai?"}
                    {step === 2 && "Brand & posizione"}
                    {step === 3 && "Dai personalità ad Aria"}
                    {step === 4 && "Eccola, la tua Aria"}
                </h1>
                <p className="text-white/55 text-[14px]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {step === 0 && "Dove deve apparire Aria? Ci servono il dominio e un'email per privacy/DPA."}
                    {step === 1 && "Scegli la categoria che meglio descrive il tuo business. Aria si pre-configura di conseguenza."}
                    {step === 2 && "Scegli i colori del brand: Aria si vestirà di quelli per integrarsi con il tuo sito."}
                    {step === 3 && "Come si chiama la tua Aria? E che tono usa quando parla?"}
                    {step === 4 && "Così apparirà sul tuo sito. Clicca 'Crea la mia Aria' per attivarla e ottenere lo snippet da installare."}
                </p>

                {/* Progress */}
                <div className="flex items-center gap-2 mt-7 mb-8">
                    {STEPS.map((s, i) => (
                        <div key={s.key} className="flex-1 flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full grid place-items-center flex-none transition" style={{
                                background: i <= step ? "linear-gradient(135deg,#A855F7,#FF1A5C)" : "rgba(255,255,255,0.06)",
                                border: i === step ? "1px solid rgba(255,255,255,0.3)" : "1px solid rgba(255,255,255,0.08)",
                            }} data-testid={`step-${i}-indicator`}>
                                {i < step ? <Check size={11} /> : <s.icon size={11} className={i === step ? "text-white" : "text-white/45"} />}
                            </div>
                            {i < STEPS.length - 1 && <div className="flex-1 h-px" style={{ background: i < step ? "linear-gradient(90deg,#A855F7,#FF1A5C)" : "rgba(255,255,255,0.06)" }} />}
                        </div>
                    ))}
                </div>

                {/* Card content */}
                <div className="rounded-2xl p-6 sm:p-8 min-h-[300px]" style={{ background: "rgba(15,18,28,0.7)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(20px)" }}>
                    <AnimatePresence mode="wait">
                        <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.25 }}>
                            {step === 0 && <StepSite form={form} upd={upd} />}
                            {step === 1 && <StepVertical form={form} upd={upd} />}
                            {step === 2 && <StepBrand form={form} upd={upd} />}
                            {step === 3 && <StepPersona form={form} upd={upd} />}
                            {step === 4 && <StepPreview form={form} selectedVertical={selectedVertical} />}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {error && <div className="mt-4 p-3 rounded-xl text-[12px]" style={{ background: "rgba(255,26,92,0.1)", color: "#FF7AA3", border: "1px solid rgba(255,26,92,0.3)" }}>{error}</div>}

                {/* Footer nav */}
                <div className="mt-6 flex items-center justify-between">
                    <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-white/65 hover:text-white text-[13px] disabled:opacity-30 transition"
                        data-testid="wizard-back">
                        <ArrowLeft size={12} /> Indietro
                    </button>
                    {step < 4 ? (
                        <button type="button" onClick={() => setStep((s) => s + 1)} disabled={!canProceed()}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-[13.5px] transition hover:scale-[1.02] disabled:opacity-30 disabled:cursor-not-allowed"
                            style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500, boxShadow: "0 18px 40px -14px rgba(168,85,247,0.4)" }}
                            data-testid="wizard-next">
                            Avanti <ArrowRight size={12} />
                        </button>
                    ) : (
                        <button type="button" onClick={submit} disabled={!canProceed() || submitting}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-[13.5px] transition hover:scale-[1.02] disabled:opacity-50"
                            style={{ background: "linear-gradient(135deg,#A855F7,#FF1A5C)", fontWeight: 500, boxShadow: "0 18px 40px -14px rgba(168,85,247,0.5)" }}
                            data-testid="wizard-submit">
                            {submitting ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
                            {submitting ? "Creo la tua Aria…" : "Crea la mia Aria"}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
function StepSite({ form, upd }) {
    return (
        <div className="space-y-5">
            <Field label="Dominio del sito" hint="Dove apparirà Aria. Es: miosito.it">
                <input type="text" value={form.site_url} onChange={(e) => upd("site_url", e.target.value)}
                    placeholder="miosito.it"
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none"
                    data-testid="input-site-url" autoFocus />
            </Field>
            <Field label="Nome del sito" hint="Come ti presenti ai visitatori. Es: 'Pizzeria Bella Napoli'">
                <input type="text" value={form.site_name} onChange={(e) => upd("site_name", e.target.value)}
                    placeholder="Pizzeria Bella Napoli"
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none"
                    data-testid="input-site-name" />
            </Field>
            <Field label="Email per privacy/DPA" hint="Dove arriveranno le richieste GDPR. Solitamente la tua email aziendale.">
                <input type="email" value={form.privacy_email} onChange={(e) => upd("privacy_email", e.target.value)}
                    placeholder="info@miosito.it"
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none"
                    data-testid="input-privacy-email" />
            </Field>
        </div>
    );
}

function StepVertical({ form, upd }) {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {VERTICALS.map((v) => {
                const selected = form.vertical === v.key;
                return (
                    <button key={v.key} type="button" onClick={() => upd("vertical", v.key)}
                        className="text-left rounded-xl p-3.5 transition hover:bg-white/[0.04]"
                        style={{
                            background: selected ? `linear-gradient(135deg, ${v.color}22, ${v.color}08)` : "rgba(255,255,255,0.03)",
                            border: `1px solid ${selected ? v.color + "70" : "rgba(255,255,255,0.06)"}`,
                            boxShadow: selected ? `0 12px 30px -10px ${v.color}40` : "none",
                        }}
                        data-testid={`vertical-${v.key}`}>
                        <div className="w-8 h-8 rounded-lg grid place-items-center mb-2" style={{ background: v.color + "22", border: `1px solid ${v.color}40` }}>
                            <v.icon size={13} style={{ color: v.color }} />
                        </div>
                        <div className="text-white text-[12.5px]" style={{ fontWeight: selected ? 500 : 400 }}>{v.label}</div>
                    </button>
                );
            })}
        </div>
    );
}

function StepBrand({ form, upd }) {
    return (
        <div className="space-y-6">
            <ColorField label="Colore primario" value={form.primary_color} onChange={(v) => upd("primary_color", v)} testid="primary-color" />
            <ColorField label="Colore secondario" value={form.secondary_color} onChange={(v) => upd("secondary_color", v)} testid="secondary-color" />

            <div>
                <label className="block text-white text-[13px] mb-1.5" style={{ fontWeight: 500 }}>Posizione del widget</label>
                <div className="grid grid-cols-2 gap-2.5">
                    {POSITIONS.map((p) => (
                        <button key={p.key} type="button" onClick={() => upd("position", p.key)}
                            className="px-4 py-3 rounded-lg text-[12.5px] transition flex items-center justify-center gap-2"
                            style={{
                                background: form.position === p.key ? "linear-gradient(135deg, #A855F7, #7C3AED)" : "rgba(255,255,255,0.04)",
                                color: form.position === p.key ? "#fff" : "rgba(255,255,255,0.75)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                fontWeight: form.position === p.key ? 500 : 400,
                            }}
                            data-testid={`position-${p.key}`}>
                            <span className="text-[16px]">{p.emoji}</span> {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Anteprima */}
            <div className="rounded-xl p-4 relative" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", minHeight: 110 }}>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 mb-2" style={{ fontFamily: "JetBrains Mono, monospace" }}>Anteprima bolla</div>
                <div className="text-white/55 text-[11.5px]">Così apparirà la bolla Aria sul tuo sito</div>
                <div className={`absolute ${form.position === "bottom-right" ? "right-4 bottom-4" : "left-4 bottom-4"} w-12 h-12 rounded-full grid place-items-center`}
                    style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})`, boxShadow: `0 12px 30px -8px ${form.primary_color}80` }}
                    data-testid="preview-bubble">
                    <Sparkles size={16} color="#fff" />
                </div>
            </div>
        </div>
    );
}

function StepPreview({ form, selectedVertical }) {
    const greetings = {
        ristorante: "Ciao, sono Aria. Ti aiuto a scegliere il piatto giusto o a prenotare un tavolo. Cosa cerchi stasera?",
        ecommerce: "Ciao, sono Aria. Ti aiuto a trovare il prodotto perfetto. Cosa stai cercando oggi?",
        bellezza: "Ciao, sono Aria. Ti aiuto a prenotare il tuo trattamento o trovare il prodotto giusto. Dimmi tutto.",
        fitness: "Ciao, sono Aria. Ti aiuto a trovare il tuo coach o l'abbonamento giusto. Qual è il tuo obiettivo?",
        studio: "Ciao, sono Aria. Ti aiuto a capire come possiamo darti supporto. Dimmi il tuo caso.",
        tipografia: "Ciao, sono Aria. Ti aiuto col preventivo o l'ordine giusto. Cosa devi stampare?",
        bar: "Ciao, sono Aria. Ti aiuto con prenotazioni, eventi o info. Cosa ti serve?",
        formazione: "Ciao, sono Aria. Ti aiuto a trovare il corso giusto. Cosa vorresti imparare?",
        servizi: "Ciao, sono Aria. Ti aiuto a capire il servizio giusto per te. Raccontami cosa cerchi.",
        altro: "Ciao, sono Aria. Sono qui per aiutarti. Dimmi cosa stai cercando.",
    };
    const greeting = greetings[form.vertical] || greetings.altro;

    return (
        <div className="space-y-6" data-testid="step-preview">
            <div className="rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 mb-3" style={{ fontFamily: "JetBrains Mono, monospace" }}>Riepilogo</div>
                <div className="grid grid-cols-2 gap-3 text-[12.5px]">
                    <SummaryRow label="Sito" value={form.site_name} />
                    <SummaryRow label="Dominio" value={form.site_url} mono />
                    <SummaryRow label="Verticale" value={selectedVertical?.label || form.vertical} />
                    <SummaryRow label="Persona" value={form.persona_name} />
                    <SummaryRow label="Tono" value={form.tone_of_voice} />
                    <SummaryRow label="Posizione" value={form.position === "bottom-right" ? "Basso destra ↘" : "Basso sinistra ↙"} />
                </div>
            </div>

            <div className="relative rounded-xl overflow-hidden" style={{ background: "linear-gradient(180deg,#0a0d18 0%,#05050F 100%)", border: "1px solid rgba(255,255,255,0.08)", minHeight: 320 }} data-testid="preview-mockup">
                <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/[0.05]" style={{ background: "rgba(255,255,255,0.03)" }}>
                    <span className="w-2 h-2 rounded-full" style={{ background: "#FF5F57" }} />
                    <span className="w-2 h-2 rounded-full" style={{ background: "#FFBD2E" }} />
                    <span className="w-2 h-2 rounded-full" style={{ background: "#28C940" }} />
                    <span className="ml-2 text-[10px] text-white/45" style={{ fontFamily: "JetBrains Mono, monospace" }}>{form.site_url || "miosito.it"}</span>
                </div>
                <div className="px-6 py-8">
                    <div className="text-white/65 text-[12px]" style={{ fontFamily: "Outfit, sans-serif" }}>// la tua pagina del sito</div>
                    <div className="text-white text-[20px] mt-2" style={{ fontWeight: 300, letterSpacing: "-0.02em" }}>{form.site_name}</div>
                    <div className="text-white/45 text-[12.5px] mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>contenuti della tua landing…</div>
                </div>
                <div className={`absolute ${form.position === "bottom-right" ? "right-6 bottom-6 items-end" : "left-6 bottom-6 items-start"} flex flex-col gap-2`} style={{ maxWidth: 300 }}>
                    <div className="rounded-2xl px-3.5 py-2.5 relative" style={{ background: "rgba(255,255,255,0.95)", color: "#1a0c00", borderRadius: form.position === "bottom-right" ? "16px 16px 4px 16px" : "16px 16px 16px 4px", boxShadow: "0 18px 40px -10px rgba(0,0,0,0.3)" }} data-testid="preview-greeting">
                        <div className="text-[11px] mb-1" style={{ fontWeight: 600, color: form.primary_color }}>{form.persona_name || "Aria"}</div>
                        <div className="text-[12.5px] leading-snug">{greeting}</div>
                    </div>
                    <div className="w-14 h-14 rounded-full grid place-items-center" style={{ background: `linear-gradient(135deg, ${form.primary_color}, ${form.secondary_color})`, boxShadow: `0 18px 40px -8px ${form.primary_color}99` }}>
                        <Sparkles size={20} color="#fff" />
                    </div>
                </div>
            </div>

            <div className="text-[11.5px] text-white/55 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>
                Aria leggerà <strong className="text-white">in tempo reale</strong> il contenuto della pagina dove si trova — niente da pre-configurare manualmente
            </div>
        </div>
    );
}

function SummaryRow({ label, value, mono }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-0.5" style={{ fontFamily: "JetBrains Mono, monospace" }}>{label}</div>
            <div className="text-white truncate" style={mono ? { fontFamily: "JetBrains Mono, monospace", fontSize: "11.5px" } : { fontSize: "13px" }}>{value || "—"}</div>
        </div>
    );
}

function StepPersona({ form, upd }) {
    return (
        <div className="space-y-5">
            <Field label="Nome di Aria" hint="Come si presenta in chat. Già pre-compilato col tuo verticale.">
                <input type="text" value={form.persona_name} onChange={(e) => upd("persona_name", e.target.value)}
                    placeholder="Aria"
                    className="w-full px-4 py-3 rounded-lg bg-white/[0.04] text-white text-[14px] border border-white/[0.08] focus:border-white/30 outline-none"
                    data-testid="input-persona-name" />
            </Field>
            <div>
                <label className="block text-white text-[13px] mb-1.5" style={{ fontWeight: 500 }}>Tono di voce</label>
                <div className="grid grid-cols-2 gap-2.5">
                    {TONES.map((t) => (
                        <button key={t.key} type="button" onClick={() => upd("tone_of_voice", t.key)}
                            className="text-left p-3.5 rounded-xl transition hover:bg-white/[0.04]"
                            style={{
                                background: form.tone_of_voice === t.key ? "linear-gradient(135deg,#A855F7,#7C3AED)" : "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                            }}
                            data-testid={`tone-${t.key}`}>
                            <div className="text-white text-[13.5px]" style={{ fontWeight: 500 }}>{t.label}</div>
                            <div className="text-white/55 text-[11.5px] mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>{t.desc}</div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="block text-white text-[13px] mb-1.5" style={{ fontWeight: 500 }}>{label}</label>
            {hint && <div className="text-white/45 text-[11.5px] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>{hint}</div>}
            {children}
        </div>
    );
}

function ColorField({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="block text-white text-[13px] mb-2" style={{ fontWeight: 500 }}>{label}</label>
            <div className="flex items-center gap-2 flex-wrap">
                <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
                    className="w-12 h-10 rounded-lg cursor-pointer bg-transparent border border-white/[0.08]"
                    data-testid={`${testid}-picker`} />
                <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
                    className="w-28 px-3 py-2.5 rounded-lg bg-white/[0.04] text-white text-[12.5px] border border-white/[0.08] focus:border-white/30 outline-none"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                    data-testid={`${testid}-hex`} />
                {PRESET_COLORS.map((c) => (
                    <button key={c} type="button" onClick={() => onChange(c)}
                        className="w-7 h-7 rounded-md transition hover:scale-110"
                        style={{ background: c, border: value === c ? "2px solid #fff" : "1px solid rgba(255,255,255,0.15)" }}
                        title={c} data-testid={`${testid}-preset-${c.replace('#', '')}`} />
                ))}
            </div>
        </div>
    );
}
